<template>
  <div class="prelim-content">
    <template v-if="loaded">
      <template v-if="currentStepIndex === PRELIM_FIXED_STEPS.AMOUNT_AND_TERM">
        <PreliminaryAmountAndTerm
          :filter-settings="filterSettings"
          :active-purchase="activePurchase"
        />
      </template>

      <template v-if="currentStepIndex === PRELIM_FIXED_STEPS.TYPE_SELECT">
        <PreliminaryTypeSelector />
      </template>

      <template
        v-if="isPsd2 && currentStepIndex === PRELIM_PSD2_FIXED_STEPS.SYNC"
      >
        <PreliminaryPsd2Sync />
      </template>

      <template v-if="currentStep">
        <p class="has-text-weight-bold is-size-4">
          {{ currentStep.name }}
        </p>

        <validation-observer ref="prelimObserver">
          <div v-for="question in currentStep.questions" :key="question.id">
            <PreliminaryRadioQuestion
              v-if="question.type === SIMPLE_FIELD_TYPE.RADIO"
              :field="question"
              :visible="isVisible(question.identifier)"
              :key="question.identifier"
            />

            <PreliminarySelectQuestion
              v-if="question.type === SIMPLE_FIELD_TYPE.SELECT"
              :field="question"
              :visible="isVisible(question.identifier)"
              :key="question.identifier"
            />

            <PreliminaryInputQuestion
              v-if="question.type === SIMPLE_FIELD_TYPE.INPUT"
              :field="question"
              :visible="isVisible(question.identifier)"
              :key="question.identifier"
            />

            <PreliminaryDatePickerQuestion
              v-if="question.type === SIMPLE_FIELD_TYPE.DATE_PICKER"
              :field="question"
              :visible="isVisible(question.identifier)"
              :key="question.identifier"
            />
          </div>
        </validation-observer>
      </template>
    </template>

    <LoadingSkeleton v-else />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  PRELIM_FIXED_STEPS,
  PRELIM_PSD2_FIXED_STEPS,
  SIMPLE_FIELD_TYPE,
} from "../utils/const";
import LoadingSkeleton from "../components/LoadingSkeleton";
import PreliminaryTypeSelector from "../components/preliminaryassessment/PreliminaryTypeSelector";
import PreliminaryPsd2Sync from "../components/preliminaryassessment/PreliminaryPsd2Sync";

export default {
  name: "PreliminaryAssessment",
  title: "af:page.preliminary_assessment.title",
  components: {
    PreliminaryTypeSelector,
    LoadingSkeleton,
    PreliminaryPsd2Sync,
    PreliminaryRadioQuestion: () =>
      import("@/components/preliminaryassessment/PreliminaryRadioQuestion.vue"),
    PreliminaryAmountAndTerm: () =>
      import("@/components/preliminaryassessment/PreliminaryAmountAndTerm.vue"),
    PreliminarySelectQuestion: () =>
      import(
        "@/components/preliminaryassessment/PreliminarySelectQuestion.vue"
      ),
    PreliminaryInputQuestion: () =>
      import("@/components/preliminaryassessment/PreliminaryInputQuestion.vue"),
    PreliminaryDatePickerQuestion: () =>
      import(
        "@/components/preliminaryassessment/PreliminaryDatePickerQuestion.vue"
      ),
  },
  data() {
    return {
      SIMPLE_FIELD_TYPE,
      PRELIM_FIXED_STEPS,
      PRELIM_PSD2_FIXED_STEPS,
      loaded: false,
    };
  },
  async created() {
    if (
      !(
        this.$store.getters["profile/isInState"]("PRELIMINARY") ||
        this.$store.getters["profile/isInState"]("ONBOARDED")
      )
    ) {
      await this.$router.push(this.$store.getters["profile/getBackPath"]);
      return;
    }

    await this.$store.dispatch("preliminaryAssessment/fetchLatest");

    if (this.type) {
      await this.$store.dispatch("preliminaryAssessment/fetchSteps", this.type);
    }

    await this.$store.dispatch("purchase/fetchActivePurchase");
    await this.$store.dispatch("filterSettings/fetchFilterSettings");

    if (this.activePurchase) {
      this.storeData("amount", this.activePurchase.loanAmount);
      this.storeData("duration", this.activePurchase.duration);
      this.storeData("downPayment", this.activePurchase.downPayment);
      this.storeData("extraAmount", this.activePurchase.extraAmount);
    } else {
      await this.prefillAmount();
      await this.prefillDuration();
    }

    this.loaded = true;
  },
  watch: {
    currentStep: {
      immediate: true,
      handler: function () {
        this.$nextTick(() => {
          this.$nextTick(() => {
            let observer = this.$refs.prelimObserver;
            if (observer) {
              observer.reset();
            }
          });
        });
      },
    },
  },
  computed: {
    ...mapGetters({
      steps: "preliminaryAssessment/steps",
      step: "preliminaryAssessment/step",
      currentStepIndex: "preliminaryAssessment/currentStepIndex",
      isVisible: "preliminaryAssessment/isVisible",
      filterSettings: "filterSettings/get",
      activePurchase: "purchase/getActivePurchase",
      isPsd2: "preliminaryAssessment/isPsd2",
      type: "preliminaryAssessment/getType",
    }),
    currentStep() {
      return this.step(this.currentStepIndex);
    },
  },
  methods: {
    getAmountFromStore(questionId) {
      return this.$store.getters["preliminaryAssessment/answer"](questionId);
    },
    storeData(questionId, value) {
      this.$store.dispatch("preliminaryAssessment/addData", {
        question: questionId,
        answer: value,
      });
    },
    async prefillAmount() {
      const storedAmount = this.getAmountFromStore("amount");

      let initialAmount = null;
      if (!storedAmount) {
        initialAmount = this.filterSettings.amountMin;
      } else {
        initialAmount = storedAmount;
      }

      this.storeData("amount", initialAmount);
    },
    async prefillDuration() {
      const storedDuration = this.getAmountFromStore("duration");

      let initialDuration = null;
      if (!storedDuration) {
        initialDuration = this.filterSettings.durationMin;
      } else {
        initialDuration = storedDuration;
      }

      this.storeData("duration", initialDuration);
    },
  },
};
</script>

<style scoped lang="scss">
.prelim-content {
  padding: 20px;
}
</style>
